/**
 * Form-field renderers per attribute schema type.
 *
 * Each input writes back through `onChange(value)`. Number/date inputs
 * convert to/from string. The enum renderer expects `options` to be set.
 *
 * Each field also runs `validateAttribute` against its schema and shows
 * the result inline: a red border on the input plus a small message
 * underneath. The same validator runs server-side before a PATCH is
 * accepted, so this is a friendly first-line feedback rather than the
 * authoritative gate.
 */
import {
  type AttributeSchema,
  type BilingualText,
  type Concept,
  isTbdSentinel,
  validateAttribute,
} from "@ae/domain";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";
import { BilingualField } from "./BilingualField";

interface FieldProps {
  schema: AttributeSchema;
  value: unknown;
  disabled?: boolean | undefined;
  lang: string;
  onChange: (value: unknown) => void;
  /**
   * Bron-Concept + branch/workspace-context — nodig voor de facet-referentie-
   * picker (create-or-pick): het nieuwe facet-object erft naam + definitie.
   */
  ownerConcept?: Concept | undefined;
  workspaceId?: string | undefined;
  branchId?: string | undefined;
}

function labelText(schema: AttributeSchema, lang: string): string {
  return schema.label[lang] ?? schema.label.nl ?? schema.label.en ?? schema.name;
}

function FieldShell({
  label,
  children,
  hint,
  errorMessage,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string | undefined;
  errorMessage?: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <span className="text-(--ld-muted)">{label}</span>
      {children}
      {errorMessage ? (
        <span className="text-(--ld-negative) text-xs">{errorMessage}</span>
      ) : (
        hint && <span className="text-(--ld-muted) text-xs">{hint}</span>
      )}
    </div>
  );
}

const inputBase =
  "rounded border px-2 py-1 text-sm focus:outline-none disabled:bg-(--ld-raised) disabled:text-(--ld-muted)";
const inputOk = `${inputBase} border-(--ld-muted) bg-(--ld-surface) text-(--ld-text) focus:border-(--ld-accent)`;
const inputBad = `${inputBase} border-(--ld-negative) bg-(--ld-negative-bg) text-(--ld-text) focus:border-(--ld-negative)`;
const inputTbd = `${inputBase} border-(--ld-notice-ink) bg-(--ld-notice-bg) text-(--ld-notice-ink) focus:border-(--ld-notice-ink)`;

export function AttributeField(props: FieldProps) {
  const { schema, value, disabled, lang, onChange, ownerConcept, workspaceId, branchId } = props;
  const { t } = useTranslation();
  const label = labelText(schema, lang);
  const hint = schema.owner_only ? t("attributePanel.fields.ownerOnly") : undefined;
  // Inline validation. Surfaced to the user via a red border + the
  // localised message underneath. Disabled fields don't show errors —
  // they're read-only views and bothering the reader serves no purpose.
  const error = disabled ? null : validateAttribute(schema, value);
  const errMsg = error?.message;
  const cls = error ? inputBad : inputOk;

  switch (schema.type) {
    case "string":
    case "string?": {
      const required = schema.type === "string";
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          <input
            className={cls}
            type="text"
            disabled={disabled}
            required={required}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            aria-invalid={error ? true : undefined}
          />
        </FieldShell>
      );
    }
    case "bilingual_string":
    case "bilingual_string?": {
      // Tweetalig veld met BilingualField — zelfde renderer als
      // base-name/description, inclusief auto-translate via
      // Claude bij blur. Backwards-compat: oude data kan
      // plain-string zijn (van vóór bilingual_string-conversie):
      // - Sentinel "TBD" / "NTB" → spread naar { nl: "NTB", en: "TBD" }
      //   zodat de gebruiker meteen taal-juiste placeholders ziet
      //   in plaats van een leeg EN-veld.
      // - Andere plain-string → in NL, EN leeg laten zodat de
      //   gebruiker zelf vertaalt (of auto-translate triggert bij blur).
      const obj = (() => {
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          return value as { nl?: string; en?: string };
        }
        if (typeof value === "string") {
          const normalized = value.trim().toLowerCase();
          if (normalized === "tbd" || normalized === "ntb") {
            return { nl: "NTB", en: "TBD" };
          }
          return { nl: value, en: "" };
        }
        return { nl: "", en: "" };
      })();
      return (
        <div className="flex flex-col gap-0.5">
          <BilingualField
            kind="description"
            labelNl={`${label} (NL)`}
            labelEn={`${label} (EN)`}
            valueNl={obj.nl ?? ""}
            valueEn={obj.en ?? ""}
            disabled={disabled}
            multiline
            onChange={(l, v) => {
              const next = { ...obj, [l]: v };
              onChange(next);
            }}
          />
          {errMsg && <span className="text-(--ld-negative) text-xs">{errMsg}</span>}
          {!errMsg && hint && <span className="text-(--ld-muted) text-xs">{hint}</span>}
        </div>
      );
    }
    case "bool":
    case "bool?":
      return (
        <label className="flex flex-col gap-0.5 text-xs">
          <span className="flex items-center gap-2">
            <span className="text-(--ld-muted)">{label}</span>
            <input
              type="checkbox"
              disabled={disabled}
              checked={value === true}
              onChange={(e) => onChange(e.target.checked)}
              aria-invalid={error ? true : undefined}
            />
            {hint && !errMsg && <span className="text-(--ld-muted) text-xs">{hint}</span>}
          </span>
          {errMsg && <span className="text-(--ld-negative) text-xs">{errMsg}</span>}
        </label>
      );
    case "int":
    case "int?":
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          <input
            className={cls}
            type="number"
            disabled={disabled}
            value={typeof value === "number" ? value : ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : Number.parseInt(e.target.value, 10))
            }
            aria-invalid={error ? true : undefined}
          />
        </FieldShell>
      );
    case "date":
    case "date?":
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          <input
            className={cls}
            type="date"
            disabled={disabled}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || null)}
            aria-invalid={error ? true : undefined}
          />
        </FieldShell>
      );
    case "enum":
    case "enum?": {
      const optional = schema.type === "enum?";
      const opts = schema.options ?? [];
      // If the schema explicitly lists "" in options, the author wants the
      // empty-value entry at that exact position (not prepended). Otherwise
      // an `enum?` gets the empty entry auto-prepended.
      const explicitEmpty = opts.includes("");
      const optionLabel = (id: string): string => {
        if (id === "") return "—";
        const map = schema.option_labels?.[id];
        if (!map) return id;
        return map[lang] ?? map.nl ?? map.en ?? id;
      };
      // Open-enum / combobox: input + datalist. Modelleur kan kiezen
      // uit suggesties (option_labels) of vrij intypen. Datalist toont
      // labels in de dropdown — bij selectie wordt de KEY in het input
      // gezet (browser default-gedrag); voor friendly UX laten we de
      // gebruiker direct de label intypen, en mappen alleen on-blur
      // terug naar key wanneer 't exact matcht. Voor onze huidige
      // freeform-use-cases (storage_provider) is dat acceptabel —
      // de keys zijn zelf al leesbaar (aws_s3, ceph, etc.).
      if (schema.freeform) {
        const listId = `enum-${schema.name}-list`;
        return (
          <FieldShell label={label} hint={hint} errorMessage={errMsg}>
            <input
              className={cls}
              type="text"
              disabled={disabled}
              list={listId}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => onChange(e.target.value || null)}
              aria-invalid={error ? true : undefined}
            />
            <datalist id={listId}>
              {opts.map((opt) => (
                <option key={opt} value={opt}>
                  {optionLabel(opt)}
                </option>
              ))}
            </datalist>
          </FieldShell>
        );
      }
      // TBD/NTB-placeholder: een verplicht keuzeveld dat nog niet is
      // ingevuld (zie fillMissingRequiredAttributes). Staat de sentinel
      // niet zelf in de options, dan tonen we 'm als extra optie bovenaan
      // — anders toont de browser stil de eerste optie alsof die gekozen
      // is. Amber rand, net als de TBD-badge in het Validatie-rapport.
      const isPlaceholder = isTbdSentinel(value) && !opts.includes(value as string);
      const absentRequired = !optional && (value === null || value === undefined || value === "");
      const showPlaceholder = isPlaceholder || (absentRequired && !explicitEmpty);
      const selectCls = isPlaceholder ? inputTbd : cls;
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          <select
            className={selectCls}
            disabled={disabled}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || null)}
            aria-invalid={error ? true : undefined}
          >
            {showPlaceholder && (
              <option value={isPlaceholder ? (value as string) : ""} disabled>
                {t("attributePanel.fields.enumTbd")}
              </option>
            )}
            {optional && !explicitEmpty && <option value="">—</option>}
            {opts.map((opt) => (
              <option key={opt} value={opt}>
                {optionLabel(opt)}
              </option>
            ))}
          </select>
        </FieldShell>
      );
    }
    case "string[]":
      // Quick render: comma-separated for M2.4. Proper add/remove UI in M2.5.
      // Lokale raw-input zodat de modelleur trailing spaces of een
      // afsluitende ", " kan typen zonder dat de round-trip
      // split+trim+filter de tekst onder de cursor wegslikt.
      return (
        <StringArrayInput
          value={Array.isArray(value) ? (value as string[]) : []}
          disabled={disabled}
          className={cls}
          label={label}
          hint={errMsg ? undefined : (hint ?? t("attributePanel.fields.commaSeparated"))}
          errorMessage={errMsg}
          ariaInvalid={error ? true : undefined}
          onChange={onChange}
        />
      );
    case "bilingual_string[]":
    case "bilingual_string[]?": {
      // Twee onafhankelijke comma-separated lijsten — een synoniem in NL
      // is doorgaans een ander woord dan in EN, dus paren-koppelen heeft
      // hier geen zin. Shape: { nl: string[], en: string[] }. Backwards-
      // compat: een legacy plain `string[]` mappen we naar de NL-lijst.
      //
      // Auto-translate: hergebruikt BilingualField met de comma-joined
      // string als payload (kind: "name"). Claude vertaalt de
      // hele lijst in één call ("klant, afnemer" → "customer, client");
      // we splitsen de response weer op komma's. Empty-target-only
      // overschrijven is in BilingualField geregeld.
      //
      // Lokale raw-state per taal zodat trailing spaces / tussen-komma's
      // tijdens typen niet door split+trim+filter worden weggevreten
      // (zelfde patroon als StringArrayInput hieronder).
      const obj = (() => {
        if (Array.isArray(value)) return { nl: value as string[], en: [] as string[] };
        if (typeof value === "object" && value !== null) {
          const o = value as { nl?: unknown; en?: unknown };
          return {
            nl: Array.isArray(o.nl) ? (o.nl as string[]) : [],
            en: Array.isArray(o.en) ? (o.en as string[]) : [],
          };
        }
        return { nl: [], en: [] };
      })();
      return (
        <BilingualStringArrayInput
          value={obj}
          disabled={disabled}
          label={label}
          hint={errMsg ? undefined : (hint ?? t("attributePanel.fields.commaSeparatedPerLanguage"))}
          errorMessage={errMsg}
          onChange={onChange}
        />
      );
    }
    case "reference":
    case "reference?": {
      const optional = schema.type === "reference?";
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          <ReferenceSingleSelect
            schema={schema}
            value={typeof value === "string" ? value : ""}
            disabled={disabled}
            lang={lang}
            cls={cls}
            optional={optional}
            onChange={onChange}
          />
        </FieldShell>
      );
    }
    case "reference[]":
    case "reference[]?": {
      const refs = Array.isArray(value) ? (value as string[]) : [];
      // Facet-referentie-veld op «sharp.Concept» krijgt de create-or-pick-picker
      // (§12.3): kies een type → bestaand voorkomen óf maak er direct één met
      // overgenomen naam + definitie. Overige reference[]-velden: plain picker.
      const facet = schema.name === "facet_references" && ownerConcept && workspaceId && branchId;
      return (
        <FieldShell label={label} hint={hint} errorMessage={errMsg}>
          {facet ? (
            <FacetReferencePicker
              schema={schema}
              value={refs}
              disabled={disabled}
              lang={lang}
              cls={cls}
              ownerConcept={ownerConcept}
              workspaceId={workspaceId}
              branchId={branchId}
              onChange={onChange}
            />
          ) : (
            <ReferenceMultiSelect
              schema={schema}
              value={refs}
              disabled={disabled}
              lang={lang}
              cls={cls}
              onChange={onChange}
            />
          )}
        </FieldShell>
      );
    }
    default:
      return (
        <FieldShell
          label={label}
          hint={t("attributePanel.fields.unsupportedType", { type: schema.type })}
        >
          <input className={inputOk} disabled type="text" value={String(value ?? "")} />
        </FieldShell>
      );
  }
}

/**
 * Verzamel de eligible-concepten voor een reference-attribuut. Filter
 * volgens `schema.reference_target`: een concept matcht wanneer z'n
 * `typeId` in `type_ids` voorkomt OF één van z'n `stereotypes` in
 * `stereotype_ids` voorkomt (union-OR). Wanneer geen filter gezet is
 * vallen alle workspace-concepten terug.
 */
function useEligibleReferenceConcepts(
  schema: AttributeSchema,
): Array<{ id: string; label: string }> {
  // Subscribe op de revision-counter (incrementeert bij elke store-mutatie)
  // zodat we re-renderen na concept-create/delete — maar pak de concept-
  // list zélf via getState() i.p.v. via selector. Een selector als
  // `s.store.listConcepts()` retourneert telkens een NIEUWE array
  // (spread van Map.values()), wat Zustand interpreteert als "altijd
  // veranderd" en in elk render-cyclus opnieuw renders triggert →
  // "maximum update depth exceeded" → white screen. Pattern hergebruikt
  // van RelationsPanel.
  const revision = useWorkspaceData((s) => s.revision);
  return useMemo(() => {
    void revision;
    const reg = getRegistry();
    const concepts = useWorkspaceData.getState().store.listConcepts();
    const filter = schema.reference_target ?? {};
    const typeIds = filter.type_ids ?? [];
    const stereoIds = filter.stereotype_ids ?? [];
    const noFilter = typeIds.length === 0 && stereoIds.length === 0;
    const out: Array<{ id: string; label: string }> = [];
    for (const c of concepts) {
      if (c.tombstoneInBranch) continue;
      if (!noFilter) {
        // Subtype-bewust: een subtype (bv. "BA" van BusinessActor) telt mee
        // als z'n parent-type in de facet-`type_ids` staat.
        const effectiveTypes = reg.effectiveTypeIdsFor(c.typeId);
        const byType = typeIds.some((t) => effectiveTypes.has(t));
        const byStereo = stereoIds.some((s) => c.stereotypes?.includes(s));
        if (!byType && !byStereo) continue;
      }
      const name = resolveBilingual(c.name, "nl") || c.id;
      out.push({ id: c.id, label: name });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [revision, schema.reference_target]);
}

function ReferenceSingleSelect({
  schema,
  value,
  disabled,
  lang: _lang,
  cls,
  optional,
  onChange,
}: {
  schema: AttributeSchema;
  value: string;
  disabled?: boolean | undefined;
  lang: string;
  cls: string;
  optional: boolean;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const eligible = useEligibleReferenceConcepts(schema);
  // Wanneer de huidige waarde een id is dat niet meer (of niet) in de
  // eligible-lijst zit — bv. omdat een ander concept verwijderd is, of
  // omdat de modelleur 'm pre-import als plain string had — toon 'm
  // alsnog als optie zodat de UI geen "—" toont waar wel een waarde
  // staat. Stale-id-label is z'n raw id zodat de modelleur kan
  // herkennen welke entry verwijderd is.
  const stale = value && !eligible.some((e) => e.id === value);
  return (
    <select
      className={cls}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value || null)}
    >
      {optional && <option value="">—</option>}
      {stale && (
        <option value={value}>
          {t("attributePanel.fields.deletedRef", { id: value.slice(0, 8) })}
        </option>
      )}
      {eligible.map((e) => (
        <option key={e.id} value={e.id}>
          {e.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Multi-select reference-picker. Render: chips met geselecteerde refs
 * (klik op × om te verwijderen) + één select-dropdown waarmee de
 * modelleur extra refs toevoegt. Geen searchable autocomplete in v1 —
 * volstaat voor de typische Risk-and-Security-use-case (paar refs per
 * attribuut).
 */
function ReferenceMultiSelect({
  schema,
  value,
  disabled,
  lang: _lang,
  cls,
  onChange,
}: {
  schema: AttributeSchema;
  value: string[];
  disabled?: boolean | undefined;
  lang: string;
  cls: string;
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation();
  const eligible = useEligibleReferenceConcepts(schema);
  const eligibleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of eligible) m.set(e.id, e.label);
    return m;
  }, [eligible]);
  const remaining = eligible.filter((e) => !value.includes(e.id));
  const addRef = (id: string) => {
    if (!id || value.includes(id)) return;
    onChange([...value, id]);
  };
  const removeRef = (id: string) => onChange(value.filter((v) => v !== id));
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-1">
        {value.length === 0 && (
          <span className="text-(--ld-muted) text-xs">{t("attributePanel.fields.none")}</span>
        )}
        {value.map((id) => {
          const label =
            eligibleById.get(id) ?? t("attributePanel.fields.deletedRef", { id: id.slice(0, 8) });
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-text)"
            >
              {label}
              {!disabled && (
                <button
                  type="button"
                  className="text-(--ld-muted) hover:text-(--ld-negative)"
                  aria-label={t("attributePanel.fields.removeRef", { label })}
                  onClick={() => removeRef(id)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>
      {!disabled && remaining.length > 0 && (
        <select
          className={cls}
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v) addRef(v);
          }}
        >
          <option value="">{t("attributePanel.fields.addRef")}</option>
          {remaining.map((e) => (
            <option key={e.id} value={e.id}>
              {e.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Facet-referentie-picker (§12.3) met create-or-pick over de businesslaag.
 * Stap 1: kies een type. Stap 2: kies een bestaand voorkomen van dat type óf
 * "Nieuw object" — dan wordt direct een plain element van dat type aangemaakt
 * met naam + definitie overgenomen van het bron-Concept en gekoppeld. Bestaat
 * er al een voorkomen met dezelfde naam, dan staat dat vooraf geselecteerd.
 */
function FacetReferencePicker({
  schema,
  value,
  disabled,
  lang,
  cls,
  ownerConcept,
  workspaceId,
  branchId,
  onChange,
}: {
  schema: AttributeSchema;
  value: string[];
  disabled?: boolean | undefined;
  lang: string;
  cls: string;
  ownerConcept: Concept;
  workspaceId: string;
  branchId: string;
  onChange: (v: string[]) => void;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const revision = useWorkspaceData((s) => s.revision);
  const [pickType, setPickType] = useState<string>("");
  const [stage2, setStage2] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const typeIds = schema.reference_target?.type_ids ?? [];
  const typeLabel = (id: string) => {
    const et = reg.getElementType(id);
    return et ? resolveBilingual(et.label, lang) : id;
  };
  const conceptName = (c: Concept) => resolveBilingual(c.name, lang) || c.id;
  const normName = (n: BilingualText) => (n.nl ?? n.en ?? "").trim().toLowerCase();

  const all = useMemo(() => {
    void revision;
    return useWorkspaceData.getState().store.listConcepts();
  }, [revision]);
  const byId = useMemo(() => {
    const m = new Map<string, Concept>();
    for (const c of all) m.set(c.id, c);
    return m;
  }, [all]);

  const instances = useMemo(
    () =>
      pickType
        ? all.filter(
            (c) =>
              // Subtype-bewust: kies je een basistype (bv. BusinessActor), dan
              // horen ook z'n subtypes (BA) erbij — een subtype IS-EEN parent.
              // Kies je een subtype, dan alleen dat subtype (exact).
              reg.effectiveTypeIdsFor(c.typeId).has(pickType) &&
              c.id !== ownerConcept.id &&
              !value.includes(c.id),
          )
        : [],
    [all, pickType, ownerConcept.id, value, reg],
  );
  const ownerNorm = normName(ownerConcept.name);
  const sameName = useMemo(
    () => instances.find((c) => normName(c.name) === ownerNorm),
    [instances, ownerNorm],
  );

  // Bij typewissel: default-selectie = bestaand voorkomen met dezelfde naam
  // (focus), anders "Nieuw object".
  useEffect(() => {
    if (!pickType) {
      setStage2("");
      return;
    }
    setStage2(sameName ? sameName.id : "__new__");
  }, [pickType, sameName]);

  const addRef = (id: string) => {
    if (id && !value.includes(id)) onChange([...value, id]);
  };
  const removeRef = (id: string) => onChange(value.filter((v) => v !== id));

  const confirm = async () => {
    if (!stage2) return;
    if (stage2 === "__new__") {
      setBusy(true);
      try {
        const created = await useWorkspaceData
          .getState()
          .createFacetConcept(workspaceId, branchId, {
            typeId: pickType,
            name: ownerConcept.name,
            ...(ownerConcept.description ? { description: ownerConcept.description } : {}),
          });
        addRef(created.id);
      } finally {
        setBusy(false);
      }
    } else {
      addRef(stage2);
    }
    setPickType("");
    setStage2("");
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-1">
        {value.length === 0 && (
          <span className="text-(--ld-muted) text-xs">{t("attributePanel.fields.none")}</span>
        )}
        {value.map((id) => {
          const c = byId.get(id);
          const label = c
            ? `${conceptName(c)} — ${typeLabel(c.typeId)}`
            : t("attributePanel.fields.deletedRef", { id: id.slice(0, 8) });
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-text)"
            >
              {label}
              {!disabled && (
                <button
                  type="button"
                  className="text-(--ld-muted) hover:text-(--ld-negative)"
                  aria-label={t("attributePanel.fields.removeRef", { label })}
                  onClick={() => removeRef(id)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
      </div>

      {!disabled && (
        <div className="flex flex-col gap-1">
          <select
            className={cls}
            value={pickType}
            disabled={busy}
            onChange={(e) => setPickType(e.target.value)}
          >
            <option value="">{t("attributePanel.fields.addFacet")}</option>
            {typeIds.map((id) => (
              <option key={id} value={id}>
                {typeLabel(id)}
              </option>
            ))}
          </select>

          {pickType && (
            <div className="flex items-center gap-1">
              <select
                className={cls}
                disabled={busy}
                value={stage2}
                onChange={(e) => setStage2(e.target.value)}
              >
                <option value="__new__">
                  {t("attributePanel.fields.newFacet", {
                    type: typeLabel(pickType),
                    name: conceptName(ownerConcept),
                  })}
                </option>
                {instances.map((c) => (
                  <option key={c.id} value={c.id}>
                    {conceptName(c)}
                    {c.typeId !== pickType ? ` — ${typeLabel(c.typeId)}` : ""}
                    {normName(c.name) === ownerNorm
                      ? t("attributePanel.fields.sameNameSuffix")
                      : ""}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="shrink-0 rounded bg-(--ld-primary) px-2 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
                disabled={busy}
                onClick={() => void confirm()}
              >
                {busy ? "…" : t("attributePanel.fields.addButton")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Comma-separated string-array input met lokale raw-state. Tijdens
 * typen wordt de input-tekst NIET getrimd of gefilterd zodat trailing
 * spaces en ", "-sequenties blijven staan terwijl de modelleur het
 * volgende item aan het invoeren is. Bij blur worden lege chunks
 * weggegooid en wordt elk chunk getrimd — pas op dat moment commit't
 * de canonieke array-vorm omhoog.
 *
 * Externe value-changes (bv. wisselen naar een ander concept of een
 * server-roundtrip) overschrijven de lokale raw-state alleen wanneer
 * de geparseerde lijst écht verschilt van wat de gebruiker heeft
 * getypt — anders zou elke debounce-flush de cursor wegslaan.
 */
function StringArrayInput({
  value,
  disabled,
  className,
  label,
  hint,
  errorMessage,
  ariaInvalid,
  onChange,
}: {
  value: string[];
  disabled?: boolean | undefined;
  className: string;
  label: string;
  hint: string | undefined;
  errorMessage: string | undefined;
  ariaInvalid: true | undefined;
  onChange: (next: string[]) => void;
}) {
  const canonical = value.join(", ");
  const [raw, setRaw] = useState<string>(canonical);
  useEffect(() => {
    // Sync down alleen wanneer externe waarde semantisch verschilt van
    // wat we lokaal hebben — voorkomt cursor-jumps bij debounce-flush.
    const parsedLocal = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const same = parsedLocal.length === value.length && parsedLocal.every((v, i) => v === value[i]);
    if (!same) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return (
    <FieldShell label={label} hint={errorMessage ? undefined : hint} errorMessage={errorMessage}>
      <input
        className={className}
        type="text"
        disabled={disabled}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          const next = raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(next);
          // Normaliseer ook de raw zodat er geen "abc , " achterblijft
          // na blur — gebruiker ziet meteen de schone vorm.
          setRaw(next.join(", "));
        }}
        aria-invalid={ariaInvalid}
      />
    </FieldShell>
  );
}

/**
 * Bilingual variant van StringArrayInput — twee onafhankelijke
 * comma-separated lijsten (NL/EN). Hergebruikt BilingualField voor
 * de auto-translate-hook; daaroverheen één laag lokale state per
 * taal zodat dezelfde trailing-space-typing-bug niet optreedt.
 *
 * Auto-translate-pad blijft canoniek: BilingualField geeft de
 * vertaalde comma-joined string terug; we splitsen + trimmen +
 * filteren op blur (en bij translate-resolve). De target-taal's
 * raw-state wordt gesynchroniseerd zodra de upstream value
 * verandert (translate-fill of concept-switch).
 */
function BilingualStringArrayInput({
  value,
  disabled,
  label,
  hint,
  errorMessage,
  onChange,
}: {
  value: { nl: string[]; en: string[] };
  disabled?: boolean | undefined;
  label: string;
  hint: string | undefined;
  errorMessage: string | undefined;
  onChange: (next: { nl: string[]; en: string[] }) => void;
}) {
  const canonicalNl = value.nl.join(", ");
  const canonicalEn = value.en.join(", ");
  const [rawNl, setRawNl] = useState<string>(canonicalNl);
  const [rawEn, setRawEn] = useState<string>(canonicalEn);
  useEffect(() => {
    const parsed = rawNl
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const same = parsed.length === value.nl.length && parsed.every((v, i) => v === value.nl[i]);
    if (!same) setRawNl(canonicalNl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalNl]);
  useEffect(() => {
    const parsed = rawEn
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const same = parsed.length === value.en.length && parsed.every((v, i) => v === value.en[i]);
    if (!same) setRawEn(canonicalEn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalEn]);
  // BilingualField triggert onChange per keystroke (geen blur exposed).
  // Om trailing whitespace tijdens typen te bewaren: commit pas wanneer
  // de getrimde + gefilterde lijst écht verandert. Een typing-in-
  // progress als "abc " parseert naar ["abc"] dat gelijk is aan de
  // huidige value — dus geen commit, raw-state blijft "abc " en de
  // input behoudt de trailing space. Zodra de modelleur een komma typt
  // verschilt de geparseerde lijst en committen we.
  const handleInput = (l: "nl" | "en", v: string) => {
    if (l === "nl") setRawNl(v);
    else setRawEn(v);
    const next = v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const current = l === "nl" ? value.nl : value.en;
    const same = next.length === current.length && next.every((s, i) => s === current[i]);
    if (!same) onChange({ ...value, [l]: next });
  };
  return (
    <div className="flex flex-col gap-0.5">
      <BilingualField
        kind="name"
        labelNl={`${label} (NL)`}
        labelEn={`${label} (EN)`}
        valueNl={rawNl}
        valueEn={rawEn}
        disabled={disabled}
        onChange={handleInput}
      />
      {errorMessage ? (
        <span className="text-(--ld-negative) text-xs">{errorMessage}</span>
      ) : (
        <span className="text-(--ld-muted) text-xs">{hint}</span>
      )}
    </div>
  );
}
